export class PatchProcessor {
	constructor(vroc) {
		this.vroc = vroc;
	}

	/**
	 * Обрабатывает патч типа 'add'
	 * @param {Object} component - Компонент VROC
	 * @param {Object} patch - Патч для обработки
	 * @param {Document} clonedXml - Клонированный XML документ
	 */
	async processAddPatch(component, patch, clonedXml) {
		console.group('PatchProcessor.processAddPatch');
		console.log('Patch:', patch);

		const { id, container, config } = component;
		const tempDiv = document.createElement('div');

		// Получаем дополнительные XPath из конфигурации компонента
		const additionalXPaths = config?.additionalXPaths || [];

		if (additionalXPaths.length > 0) {
			console.log('[PatchProcessor] Additional XPaths configured:', additionalXPaths);
		}

		const addXml = this._createXmlFromXPath(patch.xpath, patch.value, clonedXml, additionalXPaths);

		// Находим все целевые контейнеры
		const targets = this._findTargetElements(container, patch.xpath);

		const targetsByViewType = new Map();
		const targetsWithoutViewType = [];

		// Разделяем цели на те, что с view-type и без
		targets.forEach(target => {
			const viewType = target.getAttribute('data-view-type');
			if (viewType) {
				if (!targetsByViewType.has(viewType)) {
					targetsByViewType.set(viewType, []);
				}
				targetsByViewType.set(viewType, [...targetsByViewType.get(viewType), target]);
			} else {
				targetsWithoutViewType.push(target);
			}
		});

		// Обрабатываем цели с view-type
		for (const [viewType, viewTargets] of targetsByViewType) {
			tempDiv.innerHTML = '';

			await this.vroc.renderFragment(
				id,
				tempDiv,
				new DOMParser().parseFromString(addXml, "text/xml"),
				null,
				false,
				viewType
			);

			const newNode = tempDiv.querySelector(`[data-view-type="${viewType}"]`);

			if (newNode) {
				const childNodes = [...newNode.children];

				viewTargets.forEach(target => {
					if (childNodes.length > 0) {
						const nodesToAdd = target === viewTargets[viewTargets.length - 1]
							? childNodes
							: childNodes.map(node => node.cloneNode(true));

						target.append(...nodesToAdd);
					}
				});
			}
		}

		// Обрабатываем цели без view-type
		if (targetsWithoutViewType.length > 0) {
			tempDiv.innerHTML = '';

			await this.vroc.renderFragment(
				id,
				tempDiv,
				new DOMParser().parseFromString(addXml, "text/xml"),
				null,
				false
			);

			const newNode = tempDiv.querySelector(`[data-xpath="${patch.xpath}"]`);

			if (newNode) {
				const childNodes = [...newNode.children];

				targetsWithoutViewType.forEach(target => {
					if (childNodes.length > 0) {
						const nodesToAdd = target === targetsWithoutViewType[targetsWithoutViewType.length - 1]
							? childNodes
							: childNodes.map(node => node.cloneNode(true));

						target.append(...nodesToAdd);
					}
				});
			}
		}

		console.groupEnd();
	}

	/**
	 * Обрабатывает патчи типа 'update-text' и 'update-attr'
	 * @param {Object} component - Компонент VROC
	 * @param {Object} patch - Патч для обработки
	 * @param {Document} clonedXml - Клонированный XML документ
	 */
	async processUpdatePatch(component, patch, clonedXml) {
		const { id, container } = component;
		const tempDiv = document.createElement('div');

		let updateXml;
		if (patch.action === 'update-text') {
			updateXml = this._createUpdateTextXml(patch, clonedXml);
		} else if (patch.action === 'update-attr') {
			updateXml = this._createUpdateAttrXml(patch, clonedXml);
		} else {
			updateXml = patch.value;
		}

		await this.vroc.renderFragment(
			id,
			tempDiv,
			new DOMParser().parseFromString(updateXml, "text/xml")
		);

		// Обновляем все элементы с соответствующим xpath
		const targets = container.querySelectorAll(`[data-xpath="${patch.xpath}"]`);
		targets.forEach(target => {
			if (target) {
				const newContent = tempDiv.querySelector(`[data-xpath="${patch.xpath}"]`);
				if (newContent) {
					target.replaceWith(newContent.cloneNode(true));
				}
			}
		});
	}

	/**
	 * Обрабатывает патч типа 'remove'
	 * @param {Object} component - Компонент VROC
	 * @param {Object} patch - Патч для обработки
	 */
	processRemovePatch(component, patch) {
		const { container } = component;

		// Удаляем элементы с data-xpath
		const xpathNodes = container.querySelectorAll(`[data-xpath="${patch.xpath}"]`);
		xpathNodes.forEach(node => {
			// Отменяем регистрацию watch-элементов внутри удаляемого узла
			const watchElements = node.querySelectorAll('[data-xpath-watch]');
			watchElements.forEach(watchEl => {
				const xpath = watchEl.getAttribute('data-xpath-watch');
				this.vroc.watchProcessor.unregisterWatchElement(watchEl, xpath);
			});

			node.remove();
		});

		// Удаляем элементы с data-xpath-watch, соответствующие xpath патча
		const watchNodes = container.querySelectorAll(`[data-xpath-watch="${patch.xpath}"]`);
		watchNodes.forEach(node => {
			// Отменяем регистрацию watch-элемента
			const xpath = node.getAttribute('data-xpath-watch');
			this.vroc.watchProcessor.unregisterWatchElement(node, xpath);

			// Отменяем регистрацию вложенных watch-элементов
			const nestedWatchElements = node.querySelectorAll('[data-xpath-watch]');
			nestedWatchElements.forEach(watchEl => {
				const nestedXpath = watchEl.getAttribute('data-xpath-watch');
				this.vroc.watchProcessor.unregisterWatchElement(watchEl, nestedXpath);
			});

			node.remove();
		});
	}

	/**
	 * Находит целевые элементы для патча
	 * @param {HTMLElement} container - Контейнер компонента
	 * @param {string} xpath - XPath патча
	 * @returns {Array<HTMLElement>} - Массив целевых элементов
	 */
	_findTargetElements(container, xpath) {
		return Array.from(container.querySelectorAll('[data-xpath], [data-xpath-nodes]')).filter(target => {
			const targetXpath = target.getAttribute('data-xpath');
			const xpathNodes = target.getAttribute('data-xpath-nodes');

			return targetXpath === xpath ||
				(xpathNodes && xpathNodes.split(' ').some(nodePath => {
					// Проверяем, является ли patch.xpath дочерним для любого из путей в data-xpath-nodes
					return xpath.startsWith(nodePath) || nodePath.startsWith(xpath);
				}));
		});
	}

	/**
	 * Создает XML для патча типа update-text
	 * @param {Object} patch - Патч
	 * @param {Document} clonedXml - Исходный XML
	 * @returns {string} - XML строка
	 */
	_createUpdateTextXml(patch, clonedXml) {
		const xpathParts = patch.xpath.split('/').filter(part => part.length > 0);

		if (xpathParts.length > 1) {
			const xmlDoc = new DOMParser().parseFromString(
				new XMLSerializer().serializeToString(clonedXml),
				'text/xml'
			);

			const targetNode = this.vroc._evaluateXPath(patch.xpath, xmlDoc)[0];
			if (targetNode && targetNode.parentNode) {
				targetNode.textContent = patch.value;

				const immediateParent = targetNode.parentNode;
				let currentNode = immediateParent.parentNode;
				const rootNode = xmlDoc.documentElement;

				while (currentNode && currentNode !== rootNode) {
					Array.from(currentNode.children).forEach(child => {
						if (!child.contains(targetNode)) {
							currentNode.removeChild(child);
						}
					});
					currentNode = currentNode.parentNode;
				}

				if (rootNode) {
					Array.from(rootNode.children).forEach(child => {
						if (!child.contains(targetNode)) {
							rootNode.removeChild(child);
						}
					});
				}

				return new XMLSerializer().serializeToString(xmlDoc);
			}
		}

		return patch.value;
	}

	/**
	 * Создает XML для патча типа update-attr
	 * @param {Object} patch - Патч
	 * @param {Document} clonedXml - Исходный XML
	 * @returns {string} - XML строка
	 */
	_createUpdateAttrXml(patch, clonedXml) {
		const xpathParts = patch.xpath.split('/').filter(part => part.length > 0);

		let xmlStructure = '<module>';
		let currentPath = '';

		for (let i = 1; i < xpathParts.length; i++) {
			const part = xpathParts[i];
			currentPath += '/' + part;

			const originalNode = this.vroc._evaluateXPath('/module' + currentPath, clonedXml)[0];
			const { tagName } = this._parseXPathPart(part);

			xmlStructure += '<' + tagName;

			if (i === xpathParts.length - 1) {
				const tempDoc = new DOMParser().parseFromString(patch.value, 'text/xml');
				const updatedNode = tempDoc.documentElement;

				Array.from(updatedNode.attributes).forEach(attr => {
					xmlStructure += ` ${attr.name}="${attr.value}"`;
				});
				xmlStructure += '>';
			} else {
				if (originalNode) {
					Array.from(originalNode.attributes).forEach(attr => {
						xmlStructure += ` ${attr.name}="${attr.value}"`;
					});
				}
				xmlStructure += '>';
			}
		}

		for (let i = xpathParts.length - 1; i > 0; i--) {
			const { tagName } = this._parseXPathPart(xpathParts[i]);
			xmlStructure += '</' + tagName + '>';
		}

		xmlStructure += '</module>';
		return xmlStructure;
	}

	/**
	 * Создает XML из XPath и значения
	 * @param {string} xpath - XPath
	 * @param {string} value - Значение
	 * @param {Document} sourceXml - Исходный XML
	 * @param {Array<string>} additionalXPaths - Дополнительные XPath для включения в XML
	 * @returns {string} - XML строка
	 */
	_createXmlFromXPath(xpath, value, sourceXml, additionalXPaths = []) {
		const moduleElement = sourceXml.documentElement;
		const moduleAttributes = Array.from(moduleElement.attributes)
			.map(attr => `${attr.name}="${attr.value}"`)
			.join(' ');

		const parts = xpath.split('/').filter(part => part.trim() !== '');
		if (parts[0] !== 'module') {
			throw new Error('XPath должен начинаться с /module');
		}

		parts.shift();
		let xml = `<module ${moduleAttributes}>`;
		let currentPath = '';

		parts.forEach((part, index) => {
			currentPath += `/${part}`;
			const { tagName, attributes } = this._parseXPathPart(part);

			xml += `<${tagName}`;

			if (attributes) {
				for (const [name, val] of Object.entries(attributes)) {
					xml += ` ${name}="${val}"`;
				}
			}

			xml += `>`;

			if (index === parts.length - 1) {
				xml += `${value}`;
			}
		});

		for (let i = parts.length - 1; i >= 0; i--) {
			const { tagName } = this._parseXPathPart(parts[i]);
			xml += `</${tagName}>`;
		}

		// Добавляем дополнительные ветки из исходного XML
		if (additionalXPaths && additionalXPaths.length > 0) {
			additionalXPaths.forEach(additionalXPath => {
				const additionalBranch = this._extractBranchFromXml(additionalXPath, sourceXml);
				if (additionalBranch) {
					// Вставляем дополнительную ветку перед закрывающим тегом </module>
					xml += additionalBranch;
				}
			});
		}

		xml += `</module>`;
		return xml;
	}

	/**
	 * Извлекает ветку XML по XPath из исходного XML
	 * @param {string} xpath - XPath для извлечения
	 * @param {Document} sourceXml - Исходный XML документ
	 * @returns {string} - XML строка извлеченной ветки или null
	 */
	_extractBranchFromXml(xpath, sourceXml) {
		try {
			// Используем метод VROC для вычисления XPath
			const nodes = this.vroc._evaluateXPath(xpath, sourceXml);

			if (nodes && nodes.length > 0) {
				const serializer = new XMLSerializer();
				// Сериализуем все найденные узлы
				const result = nodes.map(node => serializer.serializeToString(node)).join('');
				console.log(`[PatchProcessor] Extracted branch for XPath ${xpath}:`, result.substring(0, 200) + '...');
				return result;
			} else {
				console.warn(`[PatchProcessor] Не найдены узлы для XPath: ${xpath}`);
			}
		} catch (error) {
			console.warn(`[PatchProcessor] Не удалось извлечь ветку для XPath: ${xpath}`, error);
		}
		return null;
	}

	/**
	 * Разбирает часть XPath
	 * @param {string} part - Часть XPath
	 * @returns {Object} - Объект с tagName и attributes
	 */
	_parseXPathPart(part) {
		const tagRegex = /^([^\[]+)(?:\[([^\]]+)\])?$/;
		const match = part.match(tagRegex);

		if (!match) {
			throw new Error(`Некорректная часть XPath: ${part}`);
		}

		const tagName = match[1];
		const attrString = match[2];
		let attributes = null;

		if (attrString) {
			attributes = {};
			const attrRegex = /@(\w+)='([^']*)'/g;
			let attrMatch;
			while ((attrMatch = attrRegex.exec(attrString)) !== null) {
				attributes[attrMatch[1]] = attrMatch[2];
			}
		}

		return { tagName, attributes };
	}
}
